import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/utils/supabase';
import type { Profile } from '@/utils/supabase';
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js';

// Pick only the fields we store in the auth state (subset of full Profile)
type ProfileState = Pick<Profile, 'full_name' | 'phone' | 'role' | 'preferred_language' | 'avatar_url' | 'address'>;

interface AuthState {
  user: User | null;
  profile: ProfileState | null;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setProfile: (profile: AuthState['profile']) => void;
  setLoading: (loading: boolean) => void;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: Partial<Omit<AuthState['profile'], 'id' | 'role' | 'created_at' | 'last_login'>>, signal?: AbortSignal) => Promise<boolean>;
  uploadAvatar: (file: File) => Promise<string | null>;
  signOut: () => Promise<void>;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      profile: null,
      isLoading: true,

      setUser: (user) => set({ user }),

      setProfile: (profile) => set({ profile }),

      setLoading: (isLoading) => set({ isLoading }),

      fetchProfile: async () => {
        const { user } = get();
        if (!user) {
          set({ profile: null });
          return;
        }

        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('full_name, phone, role, preferred_language, avatar_url, address')
            .eq('id', user.id)
            .single();

          if (error) throw error;
          set({ profile: data });
        } catch (error: unknown) {
          if (import.meta.env.DEV) console.error('Error fetching profile:', error);
          set({ profile: null });
        }
      },

      updateProfile: async (data, signal) => {
        const { user, profile } = get();
        if (!user) return false;

        try {
          // Whitelist allowed fields to prevent role injection
          const allowedFields = ['full_name', 'phone', 'avatar_url', 'language'] as const;
          const safeData: Record<string, string | null> = {};
          for (const key of allowedFields) {
            if (data[key as keyof typeof data] !== undefined) safeData[key] = (data[key as keyof typeof data] as string) ?? null;
          }
          if (Object.keys(safeData).length === 0) return true;

          let query = supabase
            .from('profiles')
            .update(safeData)
            .eq('id', user.id);
            
          if (signal) {
            query = query.abortSignal(signal);
          }
          
          const { error } = await query;

          if (error) throw error;
          
          set({ profile: profile ? { ...profile, ...safeData } as AuthState['profile'] : null });
          return true;
        } catch (error: unknown) {
          if (import.meta.env.DEV) console.error('Error updating profile:', error);
          return false;
        }
      },

      uploadAvatar: async (file: File) => {
        const { user } = get();
        if (!user) return null;

        if (!file.type.startsWith('image/')) {
          if (import.meta.env.DEV) console.error('Invalid file type:', file.type);
          return null;
        }

        // Compression Helper
        const compressImage = (file: File): Promise<Blob> => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
              const img = new Image();
              img.src = event.target?.result as string;
              img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                const maxDim = 500; // Sexy compressed size

                if (width > height) {
                  if (width > maxDim) {
                    height *= maxDim / width;
                    width = maxDim;
                  }
                } else {
                  if (height > maxDim) {
                    width *= maxDim / height;
                    height = maxDim;
                  }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => {
                  if (blob) resolve(blob);
                  else reject(new Error('Canvas toBlob failed'));
                }, 'image/jpeg', 0.8); // Fast and light
              };
              img.onerror = (e) => reject(e);
            };
            reader.onerror = (e) => reject(e);
          });
        };

        try {
          const compressedBlob = await compressImage(file);
          const fileExt = 'jpg'; // Convering to jpg for better compression
          const fileName = `${user.id}/${Date.now()}.${fileExt}`;
          const filePath = `${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(filePath, compressedBlob, {
              contentType: 'image/jpeg',
              upsert: true
            });

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('avatars')
            .getPublicUrl(filePath);

          return publicUrl;
        } catch (error: unknown) {
          if (import.meta.env.DEV) console.error('Error uploading avatar:', error);
          return null;
        }
      },

      signOut: async () => {
        await supabase.auth.signOut();
        set({ user: null, profile: null });
      },

      isAdmin: () => {
        const { profile } = get();
        return profile?.role === 'admin' || profile?.role === 'manager';
      },
    }),
    {
      name: 'chocolate-vibes-auth',
      partialize: (state) => ({
        user: state.user,
        // Persist only UI-relevant fields — role is always fetched fresh from server
        profile: state.profile ? {
          full_name: state.profile.full_name,
          phone: state.profile.phone,
          preferred_language: state.profile.preferred_language,
          avatar_url: state.profile.avatar_url,
          address: state.profile.address,
        } : null,
      }),
    }
  )
);

// Initialize auth state
let isInitialized = false;
export const initializeAuth = async () => {
  if (isInitialized) return;
  isInitialized = true;

  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.user) {
      useAuthStore.getState().setUser(session.user);
      await useAuthStore.getState().fetchProfile();
    }
  } catch (error: unknown) {
    if (import.meta.env.DEV) {
      // Ignore AbortError as it's often a race condition in DEV
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Failed to initialize auth session:', error);
    }
  } finally {
    useAuthStore.getState().setLoading(false);
  }

  // Listen for auth changes
  const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event: AuthChangeEvent, session: Session | null) => {
    if (_event === 'SIGNED_IN' && session?.user) {
      useAuthStore.getState().setUser(session.user);
      await useAuthStore.getState().fetchProfile();
    } else if (_event === 'SIGNED_OUT') {
      useAuthStore.getState().setUser(null);
      useAuthStore.getState().setProfile(null);
    }
  });

  return () => {
    subscription.unsubscribe();
  };
};

